import React from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';

import { useDirection } from '../hooks';
import { Glyph } from '../iconography/Glyph';
import Flex from '../layouts/Flex';
import { getInputStateType } from './Input/getInputStateType';

export const Container = styled(Flex)(
  ({ isRtl }) =>
    css({
      right: !isRtl ? 0 : 'auto',
      left: isRtl ? 0 : 'auto',
      pointerEvents: 'none',
    }),
  (props) => {
    const type = getInputStateType(props);
    return css({
      borderColor: `input.${type}.color`,
      backgroundColor: `input.${type}.bg`,
    });
  }
);

export const IconContainer = styled(Flex)(
  css({
    justifyContent: 'center',
    margin: 'auto',
    pb: 1,
    pointerEvents: 'all',
  })
);

const Icon = ({
  focus,
  invalid,
  empty,
  resetInputAriaLabel,
  invalidValueAriaLabel,
  validValueAriaLabel,
}) => {
  if (focus) {
    return (
      <Glyph
        category="UIActions"
        name="ClearCircle"
        aria-label={resetInputAriaLabel}
        color="input.branded.icon"
      />
    );
  } else if (invalid) {
    return (
      <Glyph
        category="UIActions"
        name="ClearCircle"
        aria-label={invalidValueAriaLabel}
        color="negative.default"
      />
    );
  } else if (!empty) {
    return (
      <Glyph
        category="UIActions"
        name="CheckCircle"
        aria-label={validValueAriaLabel}
        color="positive.default"
      />
    );
  } else {
    return null;
  }
};

const ValidationIcon = ({
  disabled,
  focus,
  invalid,
  isEmpty,
  reset,
  resetInputAriaLabel,
  invalidValueAriaLabel,
  validValueAriaLabel,
  stateType,
}) => {
  const isRtl = useDirection();
  // Applied to both onMouseDown and onClick to fix the area
  // not being clickable on some mobiles
  const handleClick = (e) => {
    e.preventDefault();
    if (focus) {
      reset();
    }
  };

  if (disabled) {
    return null;
  }
  return (
    <Container
      isRtl={isRtl}
      focus={focus}
      invalid={invalid}
      stateType={stateType}
      disabled={disabled}
    >
      <IconContainer onMouseDown={handleClick} onClick={handleClick}>
        <Icon
          focus={focus}
          invalid={invalid}
          empty={isEmpty}
          resetInputAriaLabel={resetInputAriaLabel}
          invalidValueAriaLabel={invalidValueAriaLabel}
          validValueAriaLabel={validValueAriaLabel}
        />
      </IconContainer>
    </Container>
  );
};

ValidationIcon.propTypes = {
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  invalid: PropTypes.bool,
  isEmpty: PropTypes.bool,
  reset: PropTypes.func,
  resetInputAriaLabel: PropTypes.string,
  invalidValueAriaLabel: PropTypes.string,
  validValueAriaLabel: PropTypes.string,
};

export default ValidationIcon;
